import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Video, MessageCircle, ShieldCheck, HeartHandshake, Brain } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_optia-telemedicine/artifacts/d09ijb3r_IMG_6632.jpeg";

const features = [
  { icon: Brain, title: "Profissionais afirmativos", desc: "Especialistas que entendem neurodivergência de verdade — sem estigma." },
  { icon: Video, title: "Consulta por vídeo", desc: "Atendimento online seguro. Sala calma, sem cadastros complicados." },
  { icon: Sparkles, title: "Aurora — apoio 24h", desc: "Assistente de IA em português para acolher e orientar entre consultas." },
  { icon: MessageCircle, title: "Chat direto", desc: "Converse com seu profissional entre as sessões, no seu tempo." },
  { icon: ShieldCheck, title: "Sigilo & LGPD", desc: "Seus dados são seus. Criptografia e conformidade com LGPD." },
  { icon: HeartHandshake, title: "Pagamento simples", desc: "Cartão via Stripe. Cancele quando quiser, sem burocracia." },
];

export default function Landing() {
  return (
    <div className="animate-fade-in">
      {/* HERO */}
      <section className="gradient-hero relative overflow-hidden">
        <div className="container-optia py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-0 rounded-full px-3 py-1" data-testid="hero-badge">
              Telemedicina afirmativa
            </Badge>
            <h1 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl text-navy-900 leading-[1.05] tracking-tight">
              Cuidado em saúde feito para mentes neurodivergentes.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              A Optia Saúde conecta você a pediatras, neuropediatras, psiquiatras da infância e adolescência, psicólogas, neuropsicólogas, terapeutas ocupacionais, fonoaudiólogas e fisioterapeutas que respeitam o seu jeito único de pensar, sentir e existir.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/profissionais">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 rounded-full px-7 h-12" data-testid="hero-cta-directory">Encontrar profissional</Button>
              </Link>
              <Link to="/cadastro">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white" data-testid="hero-cta-signup">Criar conta grátis</Button>
              </Link>
            </div>
            <div className="mt-8 max-w-xl rounded-2xl border border-teal-200 bg-white/70 backdrop-blur px-5 py-4" data-testid="hero-message">
              <h2 className="font-display font-semibold text-navy-900 text-base md:text-lg leading-snug">
                Acompanhamento do desenvolvimento e do comportamento do seu filho
              </h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Avaliação, orientação à família e encaminhamento coordenado para terapias e saúde mental. Com ou sem diagnóstico.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-teal-200/30 blur-3xl rounded-full" />
            <img src="https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Consulta online acolhedora" className="relative rounded-3xl w-full object-cover aspect-[4/5] border-4 border-white shadow-sm" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center gap-3 max-w-[240px]">
              <img src={LOGO} className="h-12 w-12 rounded-full" alt="" />
              <div>
                <p className="text-xs text-slate-500">Aurora está online</p>
                <p className="text-sm font-medium text-navy-900">"Estou aqui pra te escutar."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="container-optia py-20">
        <div className="max-w-2xl">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-navy-900">Um espaço pensado para você.</h2>
          <p className="mt-3 text-slate-600 text-lg">Sem luzes piscando, sem letras miúdas, sem pressa. Só cuidado real.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <Card key={f.title} className="card-soft hover:border-teal-300 transition-colors" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <CardContent className="p-7">
                <div className="h-11 w-11 rounded-xl bg-teal-100 grid place-items-center text-teal-700 mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-semibold text-navy-900">{f.title}</h3>
                <p className="text-slate-600 mt-2 text-[15px] leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-optia pb-8">
        <div className="rounded-3xl bg-navy-900 text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{background: "radial-gradient(600px 300px at 90% 10%, #14b8a6, transparent)"}} />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl md:text-4xl font-semibold">Você não precisa se explicar aqui.</h2>
            <p className="mt-3 text-white/80 text-lg">Encontre alguém que já entende. Primeira consulta em minutos.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/cadastro"><Button size="lg" className="bg-teal-400 hover:bg-teal-300 text-navy-900 rounded-full px-7 h-12" data-testid="cta-signup">Começar agora</Button></Link>
              <Link to="/profissionais"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10 rounded-full px-7 h-12" data-testid="cta-explore">Ver profissionais</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
